import type { SuppliesProduct } from '../../../supplies-page/api/fetch-products';
import { SUPPLIES_DISPLAY_LOCATION } from './supplies-page-constants';
import type { SuppliesDisplayLocation } from './supplies-page-constants';

/**
 * Shopify category names from the supplies store API
 */
const SUPPLIES_CATEGORIES = {
  SHIPPING_LABELS: 'Shipping Labels',
  PRINTER_CONSUMABLES: 'Printer Consumables',
  DIGITAL_SCALES: 'Digital Scales',
  BARCODE_SCANNERS: 'Barcode Scanners',
  BUNDLES: 'Bundles',
} as const;

/**
 * Maps each page to its priority categories for product display.
 * Products matching priority categories are shown first, followed by remaining products.
 * null means show all products without prioritization.
 */
const PAGE_CATEGORY_PRIORITY: Record<SuppliesDisplayLocation, string[] | null> = {
  [SUPPLIES_DISPLAY_LOCATION.HOME]: [SUPPLIES_CATEGORIES.PRINTER_CONSUMABLES, SUPPLIES_CATEGORIES.SHIPPING_LABELS],
  [SUPPLIES_DISPLAY_LOCATION.SETTINGS]: [
    SUPPLIES_CATEGORIES.PRINTER_CONSUMABLES,
    SUPPLIES_CATEGORIES.BUNDLES,
    SUPPLIES_CATEGORIES.SHIPPING_LABELS,
    SUPPLIES_CATEGORIES.DIGITAL_SCALES,
    SUPPLIES_CATEGORIES.BARCODE_SCANNERS,
  ],
  [SUPPLIES_DISPLAY_LOCATION.SETTINGS_PACKAGES]: [SUPPLIES_CATEGORIES.SHIPPING_LABELS],
  [SUPPLIES_DISPLAY_LOCATION.SETTINGS_PRINTING_CONNECT]: [SUPPLIES_CATEGORIES.PRINTER_CONSUMABLES, SUPPLIES_CATEGORIES.SHIPPING_LABELS],
  [SUPPLIES_DISPLAY_LOCATION.SETTINGS_PRINTING_SETUP]: [SUPPLIES_CATEGORIES.SHIPPING_LABELS],
  [SUPPLIES_DISPLAY_LOCATION.SUPPLIES_PAGE]: [
    SUPPLIES_CATEGORIES.PRINTER_CONSUMABLES,
    SUPPLIES_CATEGORIES.SHIPPING_LABELS,
    SUPPLIES_CATEGORIES.DIGITAL_SCALES,
    SUPPLIES_CATEGORIES.BARCODE_SCANNERS,
    SUPPLIES_CATEGORIES.BUNDLES,
  ],
};

/**
 * Prioritizes products based on the page context using round-robin interleaving.
 * Products are distributed so that at least one product from each priority category
 * appears before showing duplicates from any category. This ensures all priority
 * categories are represented even in limited display spaces.
 * @param page - The page requesting products
 * @param allProducts - All available products from the store
 * @returns Products reordered with priority categories interleaved, followed by remaining products
 */
export const getPrioritizedSuppliesProducts = (
  page: SuppliesDisplayLocation,
  allProducts: readonly SuppliesProduct[],
): SuppliesProduct[] => {
  const priorityCategories = PAGE_CATEGORY_PRIORITY[page];

  // No prioritization for this page - return all products as-is
  if (priorityCategories === null) {
    return [...allProducts];
  }

  // Group products by category
  const categoryBuckets = new Map<string, SuppliesProduct[]>();
  const remaining: SuppliesProduct[] = [];

  allProducts.forEach((product) => {
    if (priorityCategories.includes(product.categoryName)) {
      const bucket = categoryBuckets.get(product.categoryName) ?? [];
      bucket.push(product);
      categoryBuckets.set(product.categoryName, bucket);
    } else {
      remaining.push(product);
    }
  });

  // Interleave products using round-robin: one from each category per round
  const maxBucketSize = Math.max(...Array.from(categoryBuckets.values()).map((bucket) => bucket.length), 0);

  const interleaved = Array.from({ length: maxBucketSize }, (_, index) =>
    priorityCategories
      .map((category) => categoryBuckets.get(category)?.[index])
      .filter((product): product is SuppliesProduct => product !== undefined),
  ).flat();

  return [...interleaved, ...remaining];
};
